import { periodKeyToDateRange, periodKeyToTaxYearTitle } from '@/helpers/periodDisplay'
import {
  donationsForTaxYearPeriod,
  isDonorAddedDonation,
  sumDonationAmounts,
  sumGiftAidAmounts,
} from '@/helpers/taxPackDonations'
import type { DonorDonation, DonorFinancialYear } from '@/types/donorFinancialYear'
import type { TaxPackData, TaxPackPrintPayload } from '@/types/taxPack'
import type { UserStatement } from '@/types/userStatement'
import sortBy from 'lodash/sortBy'
import reverse from 'lodash/reverse'

export function isTaxPackPrintPayload(data: unknown): data is TaxPackPrintPayload {
  return Boolean(
    data &&
      typeof data === 'object' &&
      'taxPack' in data &&
      'exportOptions' in data
  )
}

/**
 * Builds statement PDF payload for one issued statement year.
 * Swiftaid totals come from the issued statement; donor-added rows are filtered by date.
 */
export function buildTaxPack(
  period: string,
  financialYear: DonorFinancialYear,
  issuedStatement: UserStatement,
  allManualDonations: DonorDonation[]
): TaxPackData {
  const swiftaidDonations = reverse(
    sortBy(
      financialYear.donations.filter((d) => !isDonorAddedDonation(d)),
      'time'
    )
  )

  const manualDonations = donationsForTaxYearPeriod(
    allManualDonations.filter(isDonorAddedDonation),
    period
  )

  const manualTotalDonated = sumDonationAmounts(manualDonations)
  const manualTotalGiftAid = sumGiftAidAmounts(manualDonations)
  const swiftaidTotalDonated = issuedStatement.totalDonated
  const swiftaidTotalGiftAid = issuedStatement.totalGiftAid
  const combinedDonations = reverse(
    sortBy([...swiftaidDonations, ...manualDonations], 'time')
  )

  return {
    period,
    taxYearTitle: periodKeyToTaxYearTitle(period),
    dateRange: periodKeyToDateRange(period),
    issuedStatement,
    swiftaidDonations,
    swiftaidTotalDonated,
    swiftaidTotalGiftAid,
    donor: issuedStatement.donor,
    manualDonations,
    manualTotalDonated,
    manualTotalGiftAid,
    combinedDonations,
    combinedTotalGiftAid: swiftaidTotalGiftAid + manualTotalGiftAid,
    totalGivingRecorded: swiftaidTotalDonated + manualTotalDonated,
    hasManualSection: manualDonations.length > 0,
  }
}
